import { useEffect, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export function AdjustmentHistory({ items, testId }) {
  if (!items.length) return <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>Nessuna rettifica registrata.</p>;
  return (
    <ul className="max-h-48 divide-y overflow-y-auto rounded-md border" data-testid={testId}>
      {items.map((a) => (
        <li key={a.id} className="flex items-start gap-3 px-3 py-2 text-xs" data-testid={`adjustment-${a.id}`}>
          <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 font-mono font-semibold ${a.amount > 0 ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"}`}>
            {a.amount > 0 ? "+" : ""}{a.amount} {a.kind === "leave" ? "gg" : "h"}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block">{a.note}</span>
            <span className="text-muted-foreground">{a.kind === "leave" ? "Ferie" : "Permessi"} {a.year} · {a.by} · {fmtDateTime(a.created_at)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function AdjustmentsSection({ user, onDone }) {
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ kind: "leave", amount: "", note: "", sign: 1 });
  const load = () => api.get(`/admin/users/${user.id}/adjustments`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    const amount = Number(f.amount) * f.sign;
    if (!amount || f.note.trim().length < 3) return toast.error("Indica quantità e motivazione (min. 3 caratteri)");
    try {
      await api.post(`/admin/users/${user.id}/adjustments`, { kind: f.kind, amount, note: f.note });
      toast.success("Rettifica registrata");
      setF({ ...f, amount: "", note: "" });
      load();
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 rounded-md border p-3" data-testid="adjustments-section">
      <p className="text-sm font-medium">Rettifiche saldo</p>
      <p className="text-xs text-muted-foreground">Saldo attuale: {user.balance?.leave_remaining} gg ferie · {user.balance?.permission_remaining} h permessi. Le rettifiche non sono modificabili e restano nello storico.</p>
      <div className="grid grid-cols-[auto_1fr_1fr] gap-2">
        <div className="flex rounded-md border">
          <Button type="button" size="icon" variant={f.sign > 0 ? "default" : "ghost"} onClick={() => setF({ ...f, sign: 1 })} data-testid="adjustment-add-sign"><Plus className="h-4 w-4" /></Button>
          <Button type="button" size="icon" variant={f.sign < 0 ? "destructive" : "ghost"} onClick={() => setF({ ...f, sign: -1 })} data-testid="adjustment-remove-sign"><Minus className="h-4 w-4" /></Button>
        </div>
        <Input type="number" min={0} step={f.kind === "leave" ? 1 : 0.25} placeholder={f.kind === "leave" ? "Giorni" : "Ore"} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="adjustment-amount-input" />
        <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
          <SelectTrigger data-testid="adjustment-kind-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="leave">Giorni di ferie</SelectItem><SelectItem value="permission">Ore di permesso</SelectItem></SelectContent>
        </Select>
      </div>
      <div className="flex gap-2">
        <Input placeholder="Motivazione (obbligatoria)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="adjustment-note-input" />
        <Button type="button" onClick={submit} data-testid="adjustment-submit-button">Registra</Button>
      </div>
      <Label className="text-xs text-muted-foreground">Storico</Label>
      <AdjustmentHistory items={items} testId="adjustments-history" />
    </div>
  );
}
